// src/components/NotificationsPanel.js
import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

export default function NotificationsPanel({ userId }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    const q = query(
      collection(db, "notifications", userId, "items"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setNotifications(list);
      setLoading(false);
    });

    return () => unsub();
  }, [userId]);

  const markAsRead = async (id) => {
    const ref = doc(db, "notifications", userId, "items", id);
    await updateDoc(ref, { read: true });
  };

  const markAllAsRead = async () => {
    const snap = await getDocs(
      collection(db, "notifications", userId, "items")
    );
    const batch = writeBatch(db);

    snap.docs.forEach((d) => {
      batch.update(d.ref, { read: true });
    });

    await batch.commit();
  };

  if (loading) {
    return <div>Loading notifications...</div>;
  }

  if (notifications.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No notifications yet.
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Inbox</h2>
        <button
          onClick={markAllAsRead}
          className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Mark all as read
        </button>
      </div>

      <div className="space-y-2">
        {notifications.map((n) => (
          <div
            key={n.id}
            className={`border rounded-lg p-3 cursor-pointer transition ${
              n.read ? "bg-white border-gray-200" : "bg-blue-50 border-blue-200"
            }`}
            onClick={() => markAsRead(n.id)}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  {!n.read && (
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-600" />
                  )}
                  <h3 className="font-medium">{n.title}</h3>
                </div>
                <div className="text-sm text-gray-700 mt-1 space-y-1">
					  {String(n.body || "")
						.split("•")
						.map((line) => line.trim())
						.filter(Boolean)
						.map((line, idx) => (
						  <div key={idx}>{line}</div>
						))}
					</div>

              </div>
              {n.createdAt && (
                <span className="text-xs text-gray-400">
                  {n.createdAt.toDate
                    ? n.createdAt.toDate().toLocaleString()
                    : ""}
                </span>
              )}
            </div>

            {/* Optional: quick action / deep links based on type */}
            {n.type === "shift_created" && n.shiftId && (
              <div className="mt-2">
                <a
                  href={`/shift/${n.shiftId}`}
                  className="text-xs text-blue-600 underline"
                >
                  View shift details
                </a>
              </div>
            )}

            {n.type === "document_verified" && (
              <div className="mt-2">
                <a
                  href="/documents"
                  className="text-xs text-blue-600 underline"
                >
                  View documents
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
